import React, { useEffect, Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { api } from './utils/api';
import { DumbbellIcon, LoaderIcon } from './components/Icons';
import AuthLanding from './pages/AuthLanding';
import AccountSwitcher from './components/AccountSwitcher';
import LoadingSpinner from './components/ui/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';

// Lazy load dashboard components for code splitting
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const PTDashboard = lazy(() => import('./pages/PTDashboard'));

function AppHeader() {
  const { user, logout } = useAuth();

  return (
    <header className="w-full p-4 flex justify-between items-center border-b border-cyan-300/20">
      <div className="flex items-center gap-2">
        <DumbbellIcon className="text-cyan-400 h-8 w-8 -rotate-45" />
        <h1 className="text-2xl font-bold text-white tracking-wider">
          AETHER<span className="text-cyan-400">FIT</span>
        </h1>
      </div>
      {user && (
        <div className="flex items-center gap-4">
          <AccountSwitcher />
          <button
            onClick={logout}
            className="ml-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            Logout
          </button>
        </div>
      )}
    </header>
  );
}

function LoadingScreen() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-cyan-300">
      <LoaderIcon className="h-8 w-8" />
      <p className="mt-4 text-lg">Initializing AetherFit...</p>
    </div>
  );
}

function AppContent() {
  const { user, loading, token } = useAuth();

  useEffect(() => {
    if (token) {
      api.setToken(token);
    }
  }, [token]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <AuthLanding />;
  }

  // Wrap lazy-loaded dashboards with Suspense and ErrorBoundary
  return (
    <ErrorBoundary>
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner size="lg" />
        </div>
      }>
        {user.role === 'pt' ? <PTDashboard /> : <ClientDashboard />}
      </Suspense>
    </ErrorBoundary>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <main className="bg-gray-900 text-gray-200 min-h-screen font-sans bg-grid-cyan-500/10">
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-gray-900 to-gray-900"></div>
        <div className="relative min-h-screen">
          <AppHeader />
          <AppContent />
        </div>
      </main>
    </AuthProvider>
  );
}